import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { getChartMetadata } from '@dashboard/constants';
import { useAppSelector } from '@app/hooks';
import { selectFilter } from '@dashboard/slices/filters';
import { useTranslation } from 'react-i18next';
import DataGrid from '@common/components/DataGrid/DataGrid';
import DataGridHeader from '@common/components/DataGrid/DataGridHeader';
import DataGridRow from '@common/components/DataGrid/DataGridRow';
import DataGridCol from '@common/components/DataGrid/DataGridCol';
import DataGridBody from '@common/components/DataGrid/DataGridBody';
import DataGridFooter from '@common/components/DataGrid/DataGridFooter';

export default function DataGridFactory({ data }) {
  const currentFilters = useAppSelector(selectFilter);
  const meta = useMemo(
    () => getChartMetadata(data.name, currentFilters),
    [data.name, currentFilters],
  );

  const { t } = useTranslation(
    meta && meta.translation ? meta.translation : '',
  );

  const dataset = useMemo(
    () => (meta && meta.parse ? meta.parse(data, t, meta) : data.dates),
    [meta, data, t],
  );

  const isDataKey = useCallback(
    (field) =>
      !!meta?.x_axis.find((v) => v.dataKey === field) ||
      !!meta?.y_axis.find((v) => v.dataKey === field),
    [meta?.x_axis, meta?.y_axis],
  );

  const sortCol = (a, b) => {
    a = typeof a === 'string' ? a : a[0];
    b = typeof b === 'string' ? b : b[0];

    if (!meta?.y_keys) {
      return 0;
    }

    const indexA = meta?.y_keys.indexOf(
      meta?.y_keys.find((v) => t(v.label) === a),
    );
    const indexB = meta?.y_keys.indexOf(
      meta?.y_keys.find((v) => t(v.label) === b),
    );
    return indexA - indexB;
  };

  const columns = useMemo(() => {
    return Object.keys(dataset[0])
      .filter((col) => col !== 'metaData')
      .sort(sortCol);
  }, [dataset]);

  const getRowEntries = (row) => {
    return Object.entries(row)
      .filter(([field]) => field !== 'metaData')
      .sort(sortCol);
  };

  const getRowTotal = (row) => {
    const total = getRowEntries(row).reduce((currentTotal, [field, value]) => {
      if (isDataKey(field) || typeof value !== 'number') return currentTotal;
      return currentTotal + value;
    }, 0);

    return meta?.tooltipFormatter ? meta.tooltipFormatter(total) : total;
  };

  const hasTotalColumn = meta?.y_keys?.length > 1 || meta?.x_keys?.length > 1;

  const totalRow = useMemo(() => {
    const totals = [];
    columns.forEach((col) => {
      if (isDataKey(col) || typeof dataset[0][col] !== 'number') {
        totals[col] = t('total');
      } else {
        totals[col] = dataset.reduce(
          (currentTotal, row) => currentTotal + row[col],
          0,
        );
      }
    });

    if (hasTotalColumn) {
      totals.total = Object.entries(totals).reduce(
        (currentTotal, [field, value]) =>
          isDataKey(field) || typeof value !== 'number'
            ? currentTotal
            : currentTotal + value,
        0,
      );
    }

    return totals;
  }, [t, columns, dataset, hasTotalColumn, isDataKey]);

  return (
    <DataGrid fullWidth>
      <DataGridHeader>
        <DataGridRow>
          {columns.map((field) => (
            <DataGridCol
              key={field}
              align={
                typeof dataset[0][field] === 'number' && !meta?.tooltipFormatter
                  ? 'right'
                  : 'center'
              }
            >
              {isDataKey(field)
                ? t(`dataKey${field.charAt(0).toUpperCase()}${field.slice(1)}`)
                : t(field)}
            </DataGridCol>
          ))}
          {hasTotalColumn && (
            <DataGridCol
              align={
                typeof getRowTotal(dataset[0]) === 'number' ? 'right' : 'center'
              }
            >
              {t('total')}
            </DataGridCol>
          )}
        </DataGridRow>
      </DataGridHeader>
      <DataGridBody>
        {dataset.map((row) => (
          <DataGridRow key={JSON.stringify(row)}>
            {getRowEntries(row).map(([field, value]) => (
              <DataGridCol
                key={field}
                align={
                  typeof value === 'number' && !meta?.tooltipFormatter
                    ? 'right'
                    : 'center'
                }
              >
                {!isDataKey(field) && meta?.tooltipFormatter
                  ? meta.tooltipFormatter(value)
                  : value}
              </DataGridCol>
            ))}
            {hasTotalColumn && (
              <DataGridCol
                align={
                  typeof getRowTotal(row) === 'number' ? 'right' : 'center'
                }
              >
                {getRowTotal(row)}
              </DataGridCol>
            )}
          </DataGridRow>
        ))}
      </DataGridBody>
      <DataGridFooter>
        <DataGridRow>
          {Object.entries(totalRow).map(([field, value]) => (
            <DataGridCol
              key={field}
              align={
                typeof value === 'number' && !meta?.tooltipFormatter
                  ? 'right'
                  : 'center'
              }
            >
              {!isDataKey(field) && meta?.tooltipFormatter
                ? meta.tooltipFormatter(value)
                : value}
            </DataGridCol>
          ))}
        </DataGridRow>
      </DataGridFooter>
    </DataGrid>
  );
}

DataGridFactory.propTypes = {
  data: PropTypes.object,
};
